import React, { useState, useEffect, useCallback } from "react";
import BasicDropDownList from "./BasicDropDownList";
import moment from "moment";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { useTranslation } from "react-i18next";

const DateSelect = ({
  label,
  name,
  value,
  onChange,
  minDate, // ✅ minAge yerine artık tarih
  maxDate,
  required,
  validationMessage,
  startYear = 1950,
  isDayVisible = false,
}) => {
  const { t } = useTranslation();
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [isValid, setIsValid] = useState(true);

  const isValidDate = (d) =>
    d instanceof Date && !isNaN(d.getTime()) && d.toString() !== "Invalid Date";

  // UTC tarih oluşturma fonksiyonu
  const createUTCDate = (year, month, day) => {
    return new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
  };

  const date = React.useMemo(
    () =>
      isValidDate(value)
        ? value
        : typeof value === "string"
        ? new Date(value)
        : createUTCDate(new Date().getFullYear(), 0, 1), // UTC ile başlangıç tarihi
    [value]
  );

  const days = React.useMemo(
    () =>
      Array.from(
        {
          length:
            date instanceof Date && !isNaN(date)
              ? moment(date).daysInMonth()
              : 31,
        },
        (_, i) => i + 1
      ),
    [date]
  );

  const months = t("COMMON_MONTHS_DATA", { returnObjects: true });
  const currentYear = new Date().getFullYear();
  const years = Array.from(
    { length: currentYear - startYear + 1 },
    (_, i) => startYear + i
  );

  useEffect(() => {
    if (isValidDate(value)) {
      setSelectedDay(date.getDate());
      setSelectedMonth(date.getMonth() + 1);
      setSelectedYear(date.getFullYear());
    }
  }, [date, value]);

  const validateDate = useCallback(
    (date) => {
      if (
        (minDate && isValidDate(minDate) && date < minDate) ||
        (maxDate && isValidDate(maxDate) && date > maxDate)
      ) {
        setIsValid(false);
      } else {
        setIsValid(true);
      }
    },
    [minDate, maxDate]
  );

  useEffect(() => {
    validateDate(date);
  }, [date, validateDate]);

  const handleDayChange = (e) => {
    setSelectedDay(e.target.value);
    // UTC tarih oluştur
    const newDate = createUTCDate(
      selectedYear || new Date().getFullYear() - 16,
      selectedMonth - 1,
      e.target.value
    );
    if (isValidDate(newDate)) onChange(newDate);
  };

  const handleMonthChange = (e) => {
    setSelectedMonth(e.target.value.value);
    // UTC tarih oluştur
    const newDate = createUTCDate(
      selectedYear || new Date().getFullYear() - 16,
      e.target.value.value - 1,
      selectedDay
    );
    if (isValidDate(newDate)) onChange(newDate);
  };

  const handleYearChange = (e) => {
    setSelectedYear(e.target.value);
    // UTC tarih oluştur
    const newDate = createUTCDate(
      e.target.value,
      selectedMonth - 1,
      selectedDay
    );
    if (isValidDate(newDate)) {
      onChange(newDate);
    }
  };

  return (
    <div>
      {label && (
        <span className="k-floating-label" style={{ display: "block" }}>
          {label}
        </span>
      )}
      <div style={{ display: "flex", alignItems: "center" }}>
        {isDayVisible && (
          <DropDownList
            data={days || []}
            onChange={handleDayChange}
            value={selectedDay}
            required={required}
            style={{ marginRight: 10 }}
            placeholder="Gün"
            valid={isValid}
          />
        )}
        <BasicDropDownList
          data={months}
          textField="text"
          valueField="value"
          onChange={handleMonthChange}
          value={selectedMonth}
          required={required}
          style={{ marginRight: 10 }}
          placeholder="Ay"
          valid={isValid}
        />
        <DropDownList
          data={years}
          onChange={handleYearChange}
          value={selectedYear}
          required={required}
          placeholder="Yıl"
          style={{ marginRight: 10 }}
          valid={isValid}
        />
      </div>
      {value && !isValid && <p style={{ color: "red" }}>{validationMessage}</p>}
    </div>
  );
};

export default DateSelect;
